import configs from "../configs.js";
import { getAuth0Token } from "../ui/utils.js";

export default class TemplatesService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    this.baseUrl = `${configs.CUSTOM_API_URL}/templates`;
  }

  /**
   * Helper method to get the correct templates URL
   * @param {string|null} workspaceId - Workspace ID or null for global templates
   * @returns {string} The URL to use
   */
  getTemplatesUrl(workspaceId) {
    if (workspaceId) {
      return `${configs.CUSTOM_API_URL}/workspaces/${workspaceId}/templates`;
    }
    return this.baseUrl;
  }

  normalizeCategory(category) {
    if (typeof category !== "string") return null;
    const normalized = category.trim();
    return normalized.length > 0 ? normalized : null;
  }

  normalizeTags(tags) {
    if (!Array.isArray(tags)) return [];
    const seen = new Set();
    const normalized = [];

    tags.forEach(tag => {
      const value = String(tag || "").trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      normalized.push(value);
    });

    return normalized;
  }

  /**
   * Get templates with filters
   * @param {Function} fetch - Fetch function
   * @param {Object} options - Options object
   * @param {Object} options.params - Query parameters (visibility, search, order_by, order_direction, per_page)
   * @param {AbortSignal} options.signal - Abort signal for cancellation
   * @param {string|null} options.workspaceId - Workspace ID for workspace-scoped templates
   * @returns {Promise<Object>} Paginated response with data, links, meta
   */
  async getTemplates(fetch, { params = {}, signal, workspaceId }) {
    const query = new URLSearchParams({
      visibility: params.visibility || "public",
      ...params
    }).toString();

    const token = await getAuth0Token();
    const url = this.getTemplatesUrl(workspaceId);
    const headers = {
      "Content-Type": "application/json"
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${url}?${query}`, {
      method: "GET",
      headers,
      signal
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Failed to fetch templates: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    const json = await response.json();

    // Return the array of templates directly for compatibility with existing code
    // If pagination info is needed, it's available in json.links and json.meta
    return json.data || [];
  }

  async getTemplate(templateId, workspaceId = null, signal) {
    if (!templateId) {
      throw new Error("Template ID is required");
    }

    const token = await getAuth0Token();
    const url = `${this.getTemplatesUrl(workspaceId)}/${templateId}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      signal
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Failed to fetch template: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    const json = await response.json();
    return json.data || json.template || json;
  }

  /**
   * Create a template from a project
   * @param {Object} project - The project to convert to template
   * @param {string} name - Template name
   * @param {string} description - Template description
   * @param {Object} options - Additional options (visibility, synchronizable, parent_project_id, workspaceId)
   * @returns {Promise<Object>} Created template object
   */
  async createTemplate(project, name, description, options = {}) {
    const { project_id, project_url, thumbnail_url } = project;

    const authUser = this.apiClient.getAuthUser();

    if (!authUser) {
      throw new Error("Not authenticated");
    }

    const token = await getAuth0Token();
    const url = this.getTemplatesUrl(options.workspaceId);

    const payload = {
      project_id,
      name: name || project.name || "Untitled Template",
      description: description || null,
      thumbnail_url: thumbnail_url || null,
      project_url: project_url || null,
      category: this.normalizeCategory(options.category),
      tags: this.normalizeTags(options.tags),
      visibility: options.visibility || "private",
      synchronizable: options.synchronizable || false,
      ...(options.sourceBranchId ? { source_branch_id: options.sourceBranchId } : {}),
      ...(options.parent_project_id && { parent_project_id: options.parent_project_id }),
      ...(options.workspaceId && { workspace_id: options.workspaceId })
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Template creation failed: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    const json = await response.json();

    // Return the template object directly
    return json.template || json.data || json;
  }

  /**
   * Update a template
   * @param {Object} template - Template object with id and fields to update
   * @param {string|null} workspaceId - Workspace ID for workspace-scoped templates
   * @returns {Promise<Object>} Updated template object
   */
  async updateTemplate(template, workspaceId = null) {
    const templateId = template.id || template.project_id;

    if (!templateId) {
      throw new Error("Template ID is required for update");
    }

    const token = await getAuth0Token();
    const url = `${this.getTemplatesUrl(workspaceId)}/${templateId}`;

    // Only send updatable fields
    const payload = {
      ...(template.name !== undefined && { name: template.name }),
      ...(template.description !== undefined && { description: template.description }),
      ...(template.thumbnail_url !== undefined && { thumbnail_url: template.thumbnail_url }),
      ...(template.project_url !== undefined && { project_url: template.project_url }),
      ...(template.category !== undefined && { category: this.normalizeCategory(template.category) }),
      ...(template.tags !== undefined && { tags: this.normalizeTags(template.tags) }),
      ...(template.synchronizable !== undefined && { synchronizable: template.synchronizable }),
      ...(template.visibility !== undefined && { visibility: template.visibility })
    };

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Template update failed: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    const json = await response.json();

    // Return the template object
    return json.data || json;
  }

  /**
   * Delete a template
   * @param {Object} template - Template object with id or project_id
   * @param {string|null} workspaceId - Workspace ID for workspace-scoped templates
   * @returns {Promise<boolean>} True if successful
   */
  async deleteTemplate(template, workspaceId = null) {
    const templateId = template.id || template.project_id;

    if (!templateId) {
      throw new Error("Template ID is required for deletion");
    }

    const token = await getAuth0Token();
    const url = `${this.getTemplatesUrl(workspaceId)}/${templateId}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Template deletion failed: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    return true;
  }

  /**
   * Synchronize a template with its parent project
   * This method can be used to update a template when the parent project changes
   * @param {Object} template - Template object
   * @param {string|null} workspaceId - Workspace ID override for workspace-scoped templates
   * @returns {Promise<Object>} Updated template and project
   */
  async synchronizeTemplate(template, workspaceId, sourceBranchId = null) {
    // Check if template is synchronizable
    if (!template.synchronizable) {
      throw new Error("Template is not synchronizable");
    }

    const hasWorkspaceOverride = arguments.length >= 2;
    const resolvedWorkspaceId = hasWorkspaceOverride
      ? workspaceId
      : template.workspace_id || template.workspace?.id || null;

    const token = await getAuth0Token();
    const url = `${this.getTemplatesUrl(resolvedWorkspaceId)}/${template.id || template.project_id}/synchronize`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        source_branch_id: sourceBranchId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Template synchronization failed: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    const json = await response.json();
    const synchronizedTemplate = json.data || json.template || json;

    return { template: synchronizedTemplate };
  }
}
